import { useState } from 'react';
import { ActionItem } from '../types';
import { actionStatusMeta } from '../meta';
import { shortDate, shortTimestamp } from '../helpers';

/**
 * A single action item's row: title/owner/TAT/status, plus an expandable comment thread. Comments
 * are a direct add (not a logged/audited edit), so there's no note modal — just an inline textarea.
 */
export function ActionItemRow({ item, onEdit, canEditRecords, displayName, onAddComment }: {
  item: ActionItem;
  onEdit: () => void;
  canEditRecords: boolean;
  displayName: (idOrLegacy: string) => string;
  onAddComment: ((text: string) => Promise<void>) | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sm = actionStatusMeta(item.status);
  const comments = item.comments || [];

  const submit = async () => {
    const text = draft.trim();
    if (!text || !onAddComment) return;
    setSubmitting(true);
    setError(null);
    try {
      await onAddComment(text);
      setDraft('');
    } catch (err: any) {
      setError(err?.message || 'Failed to add comment.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ border: '1px solid #f0f0f0', borderRadius: 8, padding: '11px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ flex: 2, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}>{item.title}</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: sm.tx, background: sm.bg, padding: '4px 10px', borderRadius: 200 }}>{item.status}</span>
          </div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{'Owner: ' + displayName(item.ownerId) + ' · Opened ' + shortDate(item.openedOn)}</div>
        </div>
        <div style={{ flex: 'none', textAlign: 'center', minWidth: 110 }}>
          <div style={{ fontSize: 11, color: '#999', marginBottom: 3 }}>{'TAT ' + item.tatDays + 'd'}</div>
          {item.breached
            ? <span style={{ fontSize: 11, fontWeight: 600, color: '#b01400', background: 'rgba(225,25,0,.12)', padding: '2px 8px', borderRadius: 5 }}>Breached</span>
            : <span style={{ fontSize: 11, fontWeight: 600, color: '#2e7d32', background: 'rgba(81,207,102,.14)', padding: '2px 8px', borderRadius: 5 }}>Within TAT</span>}
        </div>
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10, minWidth: 148, justifyContent: 'flex-end' }}>
          {canEditRecords && <span onClick={onEdit} style={{ fontSize: 12, color: '#4a90e2', cursor: 'pointer', fontWeight: 600 }}>Edit</span>}
        </div>
      </div>
      <div style={{ marginTop: 8 }}>
        <span onClick={() => setExpanded((x) => !x)} style={{ fontSize: 12, color: '#4a90e2', cursor: 'pointer', fontWeight: 600 }}>
          {(expanded ? '▾ ' : '▸ ') + 'Comments (' + comments.length + ')'}
        </span>
        {expanded && (
          <div style={{ marginTop: 8, paddingLeft: 4, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {comments.length
              ? comments.map((c) => (
                <div key={c.id} style={{ fontSize: 12, color: '#333', background: '#f8f9fa', border: '1px solid #f0f0f0', borderRadius: 6, padding: '8px 10px' }}>
                  <div>{c.text}</div>
                  <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>{displayName(c.userId) + ' · ' + shortTimestamp(c.createdAt)}</div>
                </div>
              ))
              : <div style={{ fontSize: 12, color: '#999' }}>No comments yet.</div>}
            {onAddComment && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <textarea value={draft} onChange={(ev) => setDraft(ev.target.value)} placeholder="Add a comment…"
                  style={{ width: '100%', minHeight: 56, resize: 'vertical', border: '1px solid #e0e0e0', borderRadius: 8, padding: 8, fontFamily: 'inherit', fontSize: 13, color: '#333', outline: 'none' }} />
                {error && <div style={{ fontSize: 12, color: '#b01400' }}>{error}</div>}
                <div>
                  <button onClick={submit} disabled={!draft.trim() || submitting} style={{ padding: '7px 14px', borderRadius: 7, border: 'none', background: draft.trim() && !submitting ? '#4a90e2' : '#ccc', color: '#fff', fontSize: 12, fontWeight: 600, cursor: draft.trim() && !submitting ? 'pointer' : 'not-allowed', fontFamily: 'inherit' }}>
                    {submitting ? 'Adding…' : 'Add Comment'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
